import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Shield, Trophy } from "lucide-react";
import { PageHero } from "../components/PageHero";
import { AIContent } from "../components/AIContent";
import { T } from "../context/LanguageContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function TeamsPage() {
  const [teams, setTeams] = useState([]);
  const [leagues, setLeagues] = useState([]);
  const [tab, setTab] = useState("teams");

  useEffect(() => {
    axios.get(`${API}/teams`).then((r) => setTeams(r.data)).catch(() => {});
    axios.get(`${API}/leagues`).then((r) => setLeagues(r.data)).catch(() => {});
  }, []);

  return (
    <div>
      <PageHero topic="football team celebration trophy" title="Teams, Clubs & Leagues" subtitle="Verified entity profiles for teams, clubs, leagues and competitions — with venues, founding history and governing relationships." listenTargetId="page-content" />
      <div id="page-content" className="mx-auto max-w-7xl space-y-10 px-4 py-12 sm:px-6">
        <AIContent topic="Teams, clubs and leagues — the institutions of world sport" context="Editorial about how clubs, leagues and federations connect: promotion pyramids, franchise models, community ownership and fan culture." />
        <div className="flex gap-2">
          <button data-testid="tab-teams" onClick={() => setTab("teams")} className={`rounded-full px-5 py-2 text-sm font-bold ${tab === "teams" ? "bg-[#0055FF] text-white" : "border border-slate-200 bg-white text-slate-600 hover:border-[#0055FF] hover:text-[#0055FF]"}`}><T>Teams & Clubs</T></button>
          <button data-testid="tab-leagues" onClick={() => setTab("leagues")} className={`rounded-full px-5 py-2 text-sm font-bold ${tab === "leagues" ? "bg-[#0055FF] text-white" : "border border-slate-200 bg-white text-slate-600 hover:border-[#0055FF] hover:text-[#0055FF]"}`}><T>Leagues & Competitions</T></button>
        </div>
        {tab === "teams" ? (
          <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3" data-testid="teams-grid">
            {teams.map((t) => (
              <Link to={`/teams/${t.id}`} key={t.id} data-testid={`team-card-${t.id}`} className="block rounded-2xl border border-slate-200 bg-white p-6 shadow-sm transition-all hover:-translate-y-1 hover:border-[#0055FF] hover:shadow-lg">
                <div className="flex items-start justify-between">
                  <span className="flex h-11 w-11 items-center justify-center rounded-full bg-purple-600 text-white"><Shield className="h-5 w-5" /></span>
                  <span className="rounded-full bg-blue-50 px-2.5 py-1 text-[10px] font-bold uppercase text-[#0055FF]">{t.sport}</span>
                </div>
                <h3 className="mt-3 text-lg font-bold text-slate-900">{t.name}</h3>
                <p className="text-xs font-semibold text-slate-400">{t.city}, {t.country}</p>
                <div className="mt-4 space-y-1.5 border-t border-slate-100 pt-3 text-xs text-slate-600">
                  <p><span className="font-bold"><T>League</T>:</span> {t.league}</p>
                  <p><span className="font-bold"><T>Founded</T>:</span> {t.founded}</p>
                  <p><span className="font-bold"><T>Venue</T>:</span> {t.venue}</p>
                  <p><span className="font-bold"><T>Colors</T>:</span> {t.colors}</p>
                </div>
                <p className="mt-3 text-xs font-bold text-[#0055FF]"><T>View profile</T> →</p>
              </Link>
            ))}
          </div>
        ) : (
          <div className="grid grid-cols-1 gap-5 sm:grid-cols-2" data-testid="leagues-grid">
            {leagues.map((l) => (
              <Link to={`/leagues/${l.id}`} key={l.id} data-testid={`league-card-${l.id}`} className="block rounded-2xl border border-slate-200 bg-white p-6 shadow-sm transition-all hover:-translate-y-1 hover:border-[#0055FF] hover:shadow-lg">
                <div className="flex items-start justify-between">
                  <span className="flex h-11 w-11 items-center justify-center rounded-full bg-[#0055FF] text-white"><Trophy className="h-5 w-5" /></span>
                  <span className="rounded-full bg-slate-100 px-2.5 py-1 text-[10px] font-bold uppercase text-slate-600">{l.country}</span>
                </div>
                <h3 className="mt-3 text-lg font-bold text-slate-900">{l.name}</h3>
                <p className="text-xs font-semibold text-slate-400">{l.sport} · <T>{l.level}</T></p>
                <div className="mt-4 grid grid-cols-2 gap-2 border-t border-slate-100 pt-3 text-xs text-slate-600">
                  <p><span className="font-bold"><T>Organizer</T>:</span> {l.organizer}</p>
                  <p><span className="font-bold"><T>Founded</T>:</span> {l.founded}</p>
                  {l.teams_count > 0 && <p><span className="font-bold"><T>Teams</T>:</span> {l.teams_count}</p>}
                  <p><span className="font-bold"><T>Season</T>:</span> {l.season}</p>
                </div>
                <p className="mt-3 text-xs font-bold text-[#0055FF]"><T>View profile</T> →</p>
              </Link>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
